import { useEffect, useState } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import api from '../services/api';

// Hard-coded fallback so the badge always renders, even before the
// definitions endpoint responds. The backend `services/payment_status.py`
// is the source of truth — we sync from it on first mount.
const FALLBACK_DEFINITIONS = {
  paid: 'Settled. Stripe confirmed, admin marked paid offline, or invoice was $0.',
  partial: "Part of the amount has been paid (typically via account credit) but a balance is still owed. The 'Due' figure shows what's outstanding.",
  processing: 'Customer started a Stripe payment (typically ACH/bank transfer) and Stripe is still settling it — usually 3-5 business days for ACH. No overdue reminders are sent; it flips to Paid once Stripe confirms (or Overdue if it fails).',
  pending: 'Upcoming — the invoice date is still in the future. It becomes Overdue on its invoice date if unpaid.',
  overdue: 'Unpaid on or after its invoice date. Invoices are due on the day they are dated — there is no grace period.',
  failed: 'Auto-pay attempt failed and retries were capped. Money is still owed — reconcile manually or ask the parent to update their payment method.',
  void: 'Invoice was cancelled — no money owed.',
  refunded: "The full base fee was returned to the customer. (Card payments also get the proportional card-processing surcharge back.)",
  partially_refunded: "Part of the money was returned — less than the invoice's base fee, so the customer still keeps part of what they paid. (Card refunds include the proportional surcharge on the refunded portion.)",
};

let _cachedDefinitions = null;

const STATUS_STYLES = {
  paid:        'bg-emerald-100 text-emerald-700 border-emerald-200',
  partial:     'bg-amber-100 text-amber-800 border-amber-300',
  processing:  'bg-blue-100 text-blue-700 border-blue-200',
  pending:     'bg-sky-100 text-sky-700 border-sky-200',
  overdue:     'bg-red-100 text-red-700 border-red-200',
  failed:      'bg-rose-100 text-rose-800 border-rose-300',
  void:        'bg-slate-100 text-slate-700 border-slate-300',
  refunded:    'bg-violet-100 text-violet-700 border-violet-200',
  partially_refunded: 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300',
};

const STATUS_LABELS = {
  paid: 'PAID',
  partial: 'PARTIAL',
  processing: 'PROCESSING',
  pending: 'UPCOMING',
  overdue: 'OVERDUE',
  failed: 'FAILED',
  void: 'VOID',
  refunded: 'REFUNDED',
  partially_refunded: 'PARTIALLY REFUNDED',
};

/**
 * Status pill with a built-in tooltip explaining what each canonical
 * payment status means. The text comes from
 * `GET /api/admin/payments/status-definitions` (with a static fallback)
 * so it can never drift from the backend rules.
 */
export default function PaymentStatusBadge({ status, className = '', testId }) {
  const raw = (status || '').toLowerCase();
  // Invoices are due on their invoice date. The backend only emits
  // "pending" for future-dated invoices, so it renders as UPCOMING.
  const canonical = raw === 'unpaid' ? 'overdue' : raw;
  const style = STATUS_STYLES[canonical] || 'bg-slate-100 text-slate-700 border-slate-200';
  const label = STATUS_LABELS[canonical] || canonical.toUpperCase() || 'UNKNOWN';

  const [defs, setDefs] = useState(_cachedDefinitions || FALLBACK_DEFINITIONS);

  useEffect(() => {
    if (_cachedDefinitions) return;
    api.get('/admin/payments/status-definitions')
      .then((r) => {
        if (r.data?.definitions) {
          _cachedDefinitions = r.data.definitions;
          setDefs(r.data.definitions);
        }
      })
      .catch(() => {/* keep fallback */});
  }, []);

  const tooltip = defs[canonical] || `No definition available for "${canonical}".`;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border cursor-help ${style} ${className}`}
            data-testid={testId || `status-badge-${canonical}`}
          >
            {label}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs leading-snug">
          <p className="font-semibold mb-1">{label}</p>
          <p>{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
